import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { useEffect, useState } from "react";

import { ExportDialog, lastFileFromArtifact, type ExportFormat } from "@/features/exports/export-dialog";
import type { useExportFilenames } from "@/features/exports/use-export-filenames";
import { useCanEdit } from "@/lib/capabilities/use-can-edit";

type SubmittalVersion = "generated" | "edited";

/** PDF export of the submittal log; omit where only the CSV is offered. */
export interface SubmittalPdfExport {
  latest: { artifactId: Id<"exportArtifacts">; status: string; error?: string; generatedAt?: number } | null | undefined;
  canGenerate: boolean;
  canDownload: boolean;
  queue: (fileName: string) => Promise<Id<"exportArtifacts"> | null>;
  download: (filename: string) => Promise<boolean>;
}

export interface SubmittalExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: SubmittalVersion;
  defaultFileName: string;
  pdf?: SubmittalPdfExport;
  /** Builds and downloads the CSV; resolves false when nothing was downloaded. */
  downloadCsv: (fileName: string) => boolean | Promise<boolean>;
  canDownloadCsv: boolean;
  exportFilenames: ReturnType<typeof useExportFilenames>;
}

/** Export dialog for the SpecPilot submittal log (PDF and CSV share one saved name). */
export function SubmittalExportDialog({
  open,
  onOpenChange,
  version,
  defaultFileName,
  pdf,
  downloadCsv,
  canDownloadCsv,
  exportFilenames,
}: SubmittalExportDialogProps) {
  const canEdit = useCanEdit();
  const [format, setFormat] = useState<ExportFormat>(pdf ? "pdf" : "csv");
  // Reviewers can't generate PDFs, so they start on CSV.
  useEffect(() => {
    if (open && !canEdit) setFormat("csv");
  }, [open, canEdit]);
  const [isExporting, setIsExporting] = useState(false);
  const isPdf = format === "pdf" && pdf !== undefined;

  const handleExport = async (fileName: string) => {
    setIsExporting(true);
    try {
      if (isPdf) {
        const artifactId = await pdf.queue(fileName);
        if (!artifactId) return;
        await exportFilenames.saveForArtifact(artifactId, fileName);
      } else {
        if (!(await downloadCsv(fileName))) return;
        await exportFilenames.saveBrowserExport("spec_pilot_submittal_csv", { version }, fileName);
      }
      onOpenChange(false);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <ExportDialog
      open={open}
      onOpenChange={onOpenChange}
      formats={pdf ? [{ value: "pdf" }, { value: "csv" }] : [{ value: "csv" }]}
      format={format}
      onFormatChange={setFormat}
      extension={format}
      defaultFileName={defaultFileName}
      lastFile={
        isPdf
          ? lastFileFromArtifact({
              artifact: pdf.latest,
              canDownload: pdf.canDownload,
              download: pdf.download,
              saveName: (fileName) =>
                pdf.latest ? exportFilenames.saveForArtifact(pdf.latest.artifactId, fileName) : Promise.resolve(),
            })
          : null
      }
      notice={
        isPdf && !canEdit ? "Reviewers can download the last PDF but can't generate a new one." : null
      }
      onExport={handleExport}
      isExporting={isExporting}
      exportDisabled={isPdf ? !canEdit || !pdf.canGenerate : !canDownloadCsv}
    />
  );
}
